import type { ReactNode } from 'react';
import { cn } from '../lib/cn';

export type MenuProps = {
  children: ReactNode;
  className?: string;
  /** Fixed width; menus stay narrow. */
  width?: number | string;
};

/** Floating surface for dropdowns / context menus — solid white, menu shadow, no glass. */
export function Menu({ children, className, width = 200 }: MenuProps) {
  return (
    <div
      role="menu"
      className={cn(
        'rounded-ds-lg border border-ds-border bg-ds-surface py-1 shadow-ds-menu',
        'z-30 text-ds-sm text-ds-text',
        className,
      )}
      style={{ width }}
    >
      {children}
    </div>
  );
}

export type MenuItemProps = {
  children: ReactNode;
  icon?: ReactNode;
  destructive?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  className?: string;
};

export function MenuItem({
  children,
  icon,
  destructive,
  disabled,
  onClick,
  className,
}: MenuItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex h-8 w-full items-center gap-2 px-2.5 text-left transition-colors duration-ds-fast ease-ds',
        'hover:bg-ds-subtle disabled:pointer-events-none disabled:text-ds-disabled',
        destructive ? 'text-ds-danger hover:bg-ds-danger-subtle' : 'text-ds-text',
        className,
      )}
    >
      {icon ? <span className="inline-flex shrink-0 text-ds-muted [&_svg]:size-4">{icon}</span> : null}
      <span className="truncate">{children}</span>
    </button>
  );
}

export function MenuSeparator({ className }: { className?: string }) {
  return <div role="separator" className={cn('my-1 h-px bg-ds-border', className)} />;
}
